/**
 * useGenieSetupState — derives the guided setup checklist for a genie from its
 * existing data. No new persistence: completion is read straight off the agent
 * (knowledge sources, chosen voice, brand kit), with `tested` passed in as a
 * local override the companion owns.
 *
 * Same signals the immersive SetupPopout uses, surfaced as a step-by-step arc.
 * Getting the genie out there is handled by the standalone "Activate your Genie"
 * panel, not a checklist step — so there's no "share" step here.
 */
import { useMemo } from "react";
import { Agent, DefaultConfig } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import {
  GenieSetupOverrides,
  GenieSetupState,
  GenieSetupStep,
} from "@/types/agent/genie-setup";

const hasCustomBranding = (genie: Agent): boolean =>
  Boolean(
    genie.branding &&
      (genie.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
        genie.branding.secondaryColor ||
        genie.branding.customAvatarUrl ||
        genie.branding.gradientEnabled),
  );

export const buildGenieSetupState = (
  genie: Agent | undefined,
  overrides: GenieSetupOverrides,
): GenieSetupState => {
  const knowledgeCount = genie?.knowledge_base?.length ?? 0;
  const voiceId = genie?.settings?.conversation_config?.tts?.voice_id ?? "";
  const voiceChosen = Boolean(voiceId) && voiceId !== DefaultConfig.voiceId;
  const branded = genie ? hasCustomBranding(genie) : false;
  const tested = Boolean(overrides.tested);

  const isListener = genie?.type === "listener";

  const allSteps: GenieSetupStep[] = [
    {
      id: "knowledge",
      label: "Teach it what it knows",
      hint: knowledgeCount
        ? `${knowledgeCount} ${knowledgeCount === 1 ? "source" : "sources"} in its memory`
        : "Add files, links or notes to its memory",
      genieIcon: "book-open",
      tint: "#4E9CFF",
      status: knowledgeCount > 0 ? "done" : "todo",
      target: { type: "section", anchor: "section-knowledge" },
    },
    {
      id: "voice",
      label: "Give it a voice",
      hint: voiceChosen ? "Voice chosen" : "Using a recommended voice — make it yours",
      genieIcon: "microphone",
      tint: "#FF4DB8",
      status: voiceChosen ? "done" : "ready",
      target: { type: "immersive" },
    },
    {
      id: "brand",
      label: "Make it yours",
      hint: branded ? "Brand kit applied" : "Add your logo and colours",
      genieIcon: "palette",
      tint: "#4E9CFF",
      status: branded ? "done" : "ready",
      target: { type: "section", anchor: "section-branding" },
    },
    {
      id: "phone",
      label: "Give it a phone number",
      hint: genie?.phone
        ? `Connected — ${genie.phone.number}`
        : "Let it receive or make calls",
      genieIcon: "phone",
      tint: "#4E9CFF",
      status: genie?.phone ? "done" : "todo",
      target: { type: "phone" },
    },
    {
      id: "test",
      label: "Take it for a spin",
      hint: tested ? "You've tested it live" : "Hear how it sounds on a real call",
      genieIcon: "play",
      tint: "#FF4DB8",
      status: tested ? "done" : "todo",
      target: { type: "immersive" },
    },
  ];

  const LISTENER_EXCLUDED_STEPS = new Set<string>(["knowledge", "voice", "test", "phone"]);
  const visibleSteps = isListener
    ? allSteps.filter((s) => !LISTENER_EXCLUDED_STEPS.has(s.id))
    : allSteps;

  // Skips are applied AFTER the listener filter, so a listener genie can never
  // carry a stale skip for a step it does not even have. Never overrides `done`:
  // an owner who skips the phone step and then buys a number sees it as done.
  const skipped = new Set<string>(overrides.skipped ?? []);
  const steps = visibleSteps.map((step) =>
    step.status !== "done" && skipped.has(step.id)
      ? { ...step, status: "skipped" as const }
      : step,
  );

  const totalCount = steps.length;
  const doneCount = steps.filter((s) => s.status === "done").length;
  const skippedCount = steps.filter((s) => s.status === "skipped").length;
  // A skipped step is resolved, not outstanding. If it were not, skipping would
  // never clear the "unfinished genie" takeover, which is the whole point.
  const resolvedCount = doneCount + skippedCount;

  return {
    steps,
    doneCount,
    skippedCount,
    totalCount,
    progress: totalCount ? resolvedCount / totalCount : 0,
    isComplete: resolvedCount === totalCount,
  };
};

export const useGenieSetupState = (
  genie: Agent | undefined,
  overrides: GenieSetupOverrides = {},
): GenieSetupState =>
  useMemo(
    () => buildGenieSetupState(genie, overrides),
    // Joined to a string so a fresh array literal on every render does not
    // thrash the memo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [genie, overrides.tested, (overrides.skipped ?? []).join(",")],
  );
